/**
 * Dashboard Component:
 * - Displays the main dashboard with document/user management and analytics.
 *
 * Features:
 * - Dynamic greeting based on time and day.
 * - Toggle forms for adding documents and users.
 * - Displays document counts, pending requests, recent uploads, and charts.
 *
 * Props:
 * - Uses context (AuthContext, ShortcutContext) for state and actions.
 *
 * Key Components:
 * - Navbar: Navigation bar.
 * - AddDocument: Form to add documents.
 * - AddUser: Form to add users.
 * - DocCountCard: Displays document statistics.
 * - ShortcutCards: Quick actions for admin and users.
 * - BarChartComponent: Displays document analytics.
 * - PendingRequestsCard: Shows pending document requests.
 * - RecentUploadsCard: Lists recent document uploads.
 *
 * Usage:
 * - Ensure the component is wrapped with the necessary providers.
 */

import { BarChartComponent } from "../components/charts/BarChart";
import { ProcessedTodayBarChart } from "../components/charts/ProcessedTodayBarChart";
import { useContext, useEffect, useRef, useState } from "react";
import { BsGrid3X3GapFill } from "react-icons/bs";
import Tooltip from "@mui/material/Tooltip";
import { AuthContext } from "../context/Auth/AuthContext";
import Navbar from "../components/common/Navbar";
import "../styles/Dashboard.css";
import { useShortcut } from "../context/Shortcuts/ShortcutContext";
import AddDocument from "../components/document-table/AddDocument";
import AddUser from "../components/user-table/AddUser";
import DocCountCard from "../components/dashboard/DocCountCard";
import ShortcutCards from "../components/dashboard/ShortcutCards";
import PendingRequestsCard from "../components/dashboard/PendingRequestsCard";
import RecentUploadsCard from "../components/dashboard/RecentUploadsCard";

export default function Dashboard() {
  const {
    showAddDocumentForm,
    toggleAddDocumentForm,
    setDocumentAddedToTrue,
    subCategoryOptions,
    showAddUserForm,
    onAddUserButtonClick,
    setUserAddedToTrue,
  } = useShortcut();

  const { is_admin, token, firstName, lastName, id } = useContext(AuthContext);

  const [showShortcutsPopup, setShowShortcutsPopup] = useState(false);
  const shortcutsPopupRef = useRef(null);

  useEffect(() => {
    if (!showShortcutsPopup) return;
    const handleClickOutside = (event) => {
      if (
        shortcutsPopupRef.current &&
        !shortcutsPopupRef.current.contains(event.target)
      ) {
        setShowShortcutsPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showShortcutsPopup]);

  const savedRoleId = localStorage.getItem("role_id");
  // const BACKOFFICE_TEAM_LEADER_ROLE_ID = "3bfdf38f-5084-4989-b2a8-766373cfc079";
  // const USER_ROLE_ID = "f083d7ba-4884-47bc-8514-9606f6363c12";

  const BACKOFFICE_TEAM_LEADER_ROLE_ID = "b5388e4d-a259-42cb-b071-23d1934d2c11";
  const USER_ROLE_ID = "2257087c-2dea-418f-9a8a-77b7f173f0c3";

  const onAddDocumentButtonClick = () => {
    toggleAddDocumentForm();
  };

  const handleShortcutAddDocument = () => {
    setShowShortcutsPopup(false);
    toggleAddDocumentForm();
  };

  const handleShortcutAddUser = () => {
    setShowShortcutsPopup(false);
    onAddUserButtonClick();
  };

  const greet = () => {
    let hrs = new Date().getHours();
    if (hrs < 12) return "Good Morning";
    else if (hrs >= 21) return "Good Night";
    else if (hrs >= 16) return "Good Evening";
    else return "Good Afternoon";
  };

  const greetMsg = () => {
    let day = new Date().getDay();
    return day === 0 || day === 6
      ? "HAPPY WEEKEND !!! :)"
      : "Welcome to NBL DMS";
  };

  // BOXSHADOW
  const customBoxShadow = {
    boxShadow: "0px 1px 3px rgba(15, 23, 42, 0.12)",
  };

  return (
    <>
      <div className=" flex-col md:flex w-full  overflow-y-scroll">
        <div className="navbar">
          <Navbar />
        </div>
        {showAddDocumentForm || showAddUserForm ? (
          <div className="document-table w-full  p-8 pt-6">
            {/* ADD DOCUMENT FORM  */}
            {showAddDocumentForm && (
              <AddDocument
                onAddDocumentButtonClick={onAddDocumentButtonClick}
                setDocumentAddedToTrue={setDocumentAddedToTrue}
                //categoryOptions={categoryOptions}
                subCategoryOptions={subCategoryOptions}
              />
            )}
            {/* ADD USER FORM  */}
            {showAddUserForm && (
              <AddUser
                onAddUserButtonClick={onAddUserButtonClick}
                setUserAddedToTrue={setUserAddedToTrue}
              />
            )}
          </div>
        ) : (
          <div className="flex-1 space-y-4 p-8 pt-6 w-full">
            <div className="flex items-center justify-between gap-4 w-full p-6 bg-gradient-to-r from-[#0d2f3d] to-[#1a6b7c] rounded-lg shadow-md">
              <div className="flex flex-col gap-1">
                <p className="text-2xl font-semibold text-white">{`${greet()}, ${firstName} ${lastName}`}</p>
                <p className="text-base text-white/80 italic">{greetMsg()}</p>
              </div>
              <div className="hidden sm:flex flex-col items-center justify-center w-20 h-20 rounded-full border-2 border-white/40 text-white shrink-0">
                <span className="text-sm font-semibold uppercase">
                  {new Date().toLocaleString("en-US", { month: "short" })}{" "}
                  {new Date().toLocaleString("en-US", { day: "2-digit" })}
                </span>
                <span className="text-xs">{new Date().getFullYear()}</span>
              </div>
            </div>

            <DocCountCard
              customBoxShadow={customBoxShadow}
              token={token}
              is_admin={is_admin}
              userId={id}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              {savedRoleId === BACKOFFICE_TEAM_LEADER_ROLE_ID && (
                <div className="md:col-span-2">
                  <ProcessedTodayBarChart
                    customBoxShadow={customBoxShadow}
                    token={token}
                  />
                </div>
              )}
              {savedRoleId !== USER_ROLE_ID &&
                savedRoleId !== BACKOFFICE_TEAM_LEADER_ROLE_ID && (
                  <>
                    <BarChartComponent
                      customBoxShadow={customBoxShadow}
                      token={token}
                    />
                    <ProcessedTodayBarChart
                      customBoxShadow={customBoxShadow}
                      token={token}
                    />
                    {/* <PendingRequestsCard
                      customBoxShadow={customBoxShadow}
                      token={token}
                      is_admin={is_admin}
                    />

                    <RecentUploadsCard
                      customBoxShadow={customBoxShadow}
                      token={token}
                      is_admin={is_admin}
                    /> */}
                  </>
                )}
            </div>

            {/*
              Temporarily commented out the original Pending Requests section
              so Pending Requests can be shown in the Reports area instead.

            {savedRoleId !== USER_ROLE_ID &&
              savedRoleId !== BACKOFFICE_TEAM_LEADER_ROLE_ID && (
                <>
                  <div className="w-full">
                    <PendingRequestsCard
                      customBoxShadow={customBoxShadow}
                      token={token}
                      is_admin={is_admin}
                    />
                  </div>
                </>
              )}
            */}
          </div>
        )}
      </div>

      {/* Floating Shortcuts button — fixed to the viewport's bottom-right
          corner so it stays reachable regardless of dashboard scroll. */}
      <div className="fixed bottom-6 right-6 z-50" ref={shortcutsPopupRef}>
        {showShortcutsPopup && (
          <div className="absolute bottom-16 right-0">
            <ShortcutCards
              toggleAddDocumentForm={handleShortcutAddDocument}
              onAddUserButtonClick={handleShortcutAddUser}
              is_admin={is_admin}
            />
          </div>
        )}
        <Tooltip title="Shortcuts" placement="left" arrow>
          <button
            type="button"
            onClick={() => setShowShortcutsPopup((prev) => !prev)}
            className="w-14 h-14 rounded-full bg-[#1a6b7c] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105 hover:bg-[#0d2f3d]"
          >
            <BsGrid3X3GapFill className="text-xl" />
          </button>
        </Tooltip>
      </div>
    </>
  );
}
